// HR chrome — ONE route tree (`/hr/*`), two layouts chosen by VIEWPORT
// (PO 2026-07-31). Below `md` the bottom-nav app shell; at `md` and above the
// existing desktop `Shell` (sidebar at `lg`, pill top nav between).
//
// Third and last of the responsive conversions, after Manager and Employee,
// and the same story: the bottom-nav shell lived on its own UA-gated
// `/hr/mobile/*` tree that almost nothing reached. HR's was the most stranded
// of the three — `landingPathFor` deliberately always sends HR to `/hr`
// regardless of device ("HR is always a desktop workflow", PO 2026-07-30), so
// `/hr/mobile` had NO entry path at all beyond someone typing the URL, and its
// `deviceGuardLoader` only ever bounced desktop UAs back out of a tree they
// couldn't have reached anyway. The PO decision it rests on is about the
// LANDING PATH, which is unchanged — HR still lands on `/hr`. What changes is
// that `/hr` now renders the approved app shell when it's on a phone.
//
// With this, no User-Agent routing remains anywhere in the tenant SPA.
//
// The tab bar is Home / Ask Maeve / Pathways / More: HR's desktop nav has nine
// items, so "More" (`HrMore.tsx`) carries the other six plus sign-out — which
// the app shell has nowhere else to put.

import { matchPath, useLocation } from 'react-router';
import { Shell, FULL_BLEED_CONTENT_CLASSNAME } from '@/components/Shell.tsx';
import type { NavItem } from '@/components/AppNav.tsx';
import { useSession } from '@/lib/auth.ts';
import type { MobileTab } from '@/components/MobileTabBar.tsx';
import {
  GridIcon,
  UsersIcon,
  PlusIcon,
  UserIcon,
  DocIcon,
  BookIcon,
  ChatIcon,
  PlugIcon,
  SlidersIcon,
  HomeIcon,
  BarsIcon,
} from '@/components/ui/icons.tsx';

const HR_TABS: MobileTab[] = [
  { to: '/hr', label: 'Home', icon: <HomeIcon size={22} />, end: true },
  { to: '/hr/ask-maeve', label: 'Ask Maeve', icon: <ChatIcon size={22} /> },
  // "Pathways", not the full "Support pathways" the desktop nav uses (PO
  // 2026-08-06): four `flex-1` tabs inside a `max-w-md` bar at 375px leave
  // ~94px per label at 0.65rem, and `MobileTabBar` has no truncation — the
  // full term would wrap. The short form only appears in this one constrained
  // slot; every other surface says "support pathway" in full.
  { to: '/hr/cases', label: 'Pathways', icon: <UsersIcon size={22} /> },
  { to: '/hr/more', label: 'More', icon: <BarsIcon size={22} /> },
];

// The one page that paints edge-to-edge inside the app shell: the Maeve chat
// owns the full viewport between header and tab bar and scrolls internally,
// so the shell must not add page padding or a second scroll container around
// it. Every other `/hr/*` page is a normal padded document. Same pattern as
// `ManagerShell`/`Portal` — see either for the reasoning.
const FULL_BLEED_PATHS = new Set(['/hr/ask-maeve']);

export function HrShell() {
  const session = useSession('hr-shell');
  const { pathname } = useLocation();
  // Full-bleed BELOW `md` only — at `md`+ the chat is a bounded column inside
  // the normal padded page (see `HrAskMaeve`). Expressed as classNames so the
  // tree stays constant.
  const fullBleed = FULL_BLEED_PATHS.has(pathname.replace(/\/$/, ''));
  // "New support pathway" is HR_Admin-only (slice 8 — DESIGN §6.1) — HR_Viewer
  // is read-only per DESIGN §1.2 and the API gate would 403 anyway.
  const nav: NavItem[] = [
    { to: '/hr', label: 'Overview', end: true, icon: <GridIcon /> },
    // `end: false` so the detail view (`/hr/cases/:id`) still lights this
    // up, but that means it would ALSO match `/hr/cases/new` — a separate,
    // more specific nav item below — without the explicit exclusion. Built on
    // `matchPath` (the router's own matching engine, same one `router.tsx`'s
    // routes resolve against) rather than a hand-rolled regex — an earlier
    // regex-based version of this exclusion had a trailing-slash gap
    // (`/hr/cases/new/` wasn't excluded) that `matchPath`'s `end: true` on the
    // excluded pattern does not have.
    {
      to: '/hr/cases',
      label: 'Support pathways',
      icon: <UsersIcon />,
      isActive: (pathname) =>
        !!matchPath({ path: '/hr/cases', end: false }, pathname) &&
        !matchPath({ path: '/hr/cases/new', end: true }, pathname),
    },
    ...(session.role === 'HR_Admin'
      ? [{ to: '/hr/cases/new', label: 'New support pathway', icon: <PlusIcon /> }]
      : []),
    { to: '/hr/members', label: 'Members', icon: <UserIcon /> },
    { to: '/hr/policies', label: 'Policies', icon: <DocIcon /> },
    // §15 — support resources. Visible to both HR roles; mutations are
    // HR_Admin-gated server-side (HR_Viewer sees the list read-only).
    { to: '/hr/resources', label: 'Resources', icon: <BookIcon /> },
    // Slice 11 — visible to both HR_Admin and HR_Viewer; the backend
    // accepts both roles per DESIGN §10.1.1 (HR mode is the structured-
    // retrieval surface for the HR tier, not the mutation surface).
    { to: '/hr/ask-maeve', label: 'Ask Maeve', icon: <ChatIcon /> },
    // Slice 21 — workspace integrations (Slack first). Visible to both
    // HR roles; HR_Viewer sees the status read-only, mutations are
    // HR_Admin-gated server-side.
    { to: '/hr/integrations', label: 'Integrations', icon: <PlugIcon /> },
    // Org profile + read-only privacy config. Visible to both HR roles;
    // edits are HR_Admin-gated server-side (HR_Viewer sees the form
    // disabled).
    { to: '/hr/settings', label: 'Settings', icon: <SlidersIcon /> },
  ];
  return (
    <Shell
      chipLabel="HR TOOLS"
      nav={nav}
      routeId="hr-shell"
      tabs={HR_TABS}
      tabsLabel="HR"
      breakpoint="md"
      contentClassName={
        fullBleed
          ? FULL_BLEED_CONTENT_CLASSNAME.md
          : 'mx-auto w-full max-w-6xl px-4 py-5 md:py-6 lg:px-8 lg:py-10'
      }
    />
  );
}
